import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Search, Loader2, X, Smartphone, Trash2, Users as UsersIcon, CreditCard, ShoppingBag, BadgeCheck } from 'lucide-react';
import api, { formatApiError } from '@/lib/adminApi';
import { EmptyState } from '@/components/admin/EmptyState';
import { ADMIN } from '@/constants/testIds';

const PLATFORM_COLOR = { iOS: 'bg-white/10 text-white', android: 'bg-success/15 text-success' };
const displayId = (id) => (id.startsWith('$RCAnonymousID:') ? `anon…${id.slice(-8)}` : id.length > 24 ? `${id.slice(0, 12)}…${id.slice(-6)}` : id);
const fmtDate = (ms) => (ms ? new Date(ms).toLocaleString() : '—');

const DetailRow = ({ label, value }) => (
  <div className="rounded-xl border border-white/10 bg-elevated px-3 py-2">
    <p className="text-xs text-white/40">{label}</p>
    <p className="mt-0.5 break-all text-sm font-medium text-white">{value ?? '—'}</p>
  </div>
);

const DetailSection = ({ icon: Icon, title, items, render, empty }) => (
  <div className="mt-5">
    <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white/40"><Icon className="h-3.5 w-3.5" /> {title} ({items.length})</p>
    {items.length === 0 ? <p className="text-sm text-white/40">{empty}</p> : items.map(render)}
  </div>
);

const UserDrawer = ({ userId, onClose, onDeleted }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/admin/revenuecat/customers/${encodeURIComponent(userId)}`)
      .then((r) => setDetail(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [userId]);

  const remove = async () => {
    if (!window.confirm(`Permanently delete this user from RevenueCat?\n\n${userId}\n\nThis erases their purchase history and entitlements. It cannot be undone.`)) return;
    try {
      await api.delete(`/admin/revenuecat/customers/${encodeURIComponent(userId)}`);
      toast.success('User deleted from RevenueCat');
      onDeleted(userId);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const c = detail?.customer;
  const attrs = c?.attributes?.items || [];
  const email = attrs.find((a) => a.name === '$email')?.value;
  const name = attrs.find((a) => a.name === '$displayName')?.value;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div data-testid={ADMIN.userDetail} onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-md overflow-y-auto border-l border-white/10 bg-surface p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand/15 text-brand"><Smartphone className="h-5 w-5" /></div>
            <div>
              <h2 className="font-display text-lg font-bold text-white">{name || email || displayId(userId)}</h2>
              <p className="break-all font-mono text-[11px] text-white/40">{userId}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        {error ? (
          <p className="mt-8 text-sm text-destructive">{error}</p>
        ) : !detail ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <DetailRow label="Platform" value={c.last_seen_platform} />
              <DetailRow label="Country" value={c.last_seen_country} />
              <DetailRow label="App version" value={c.last_seen_app_version} />
              <DetailRow label="OS" value={c.last_seen_platform_version} />
              <DetailRow label="First seen" value={fmtDate(c.first_seen_at)} />
              <DetailRow label="Last seen" value={fmtDate(c.last_seen_at)} />
              {email && <DetailRow label="Email" value={email} />}
            </div>

            {attrs.length > 0 && (
              <DetailSection icon={BadgeCheck} title="Attributes" items={attrs} empty=""
                render={(a) => (
                  <div key={a.name} className="mb-2 rounded-xl border border-white/10 bg-elevated px-3 py-2 text-sm">
                    <span className="text-white/40">{a.name}:</span> <span className="break-all text-white">{a.value}</span>
                  </div>
                )} />
            )}

            <DetailSection icon={BadgeCheck} title="Active entitlements" items={detail.entitlements} empty="No active entitlements."
              render={(e) => (
                <div key={e.entitlement_id || e.id} className="mb-2 rounded-xl border border-success/20 bg-success/5 px-3 py-2 text-sm">
                  <p className="text-white">{e.entitlement_id || e.id}</p>
                  {e.expires_at && <p className="text-xs text-white/40">Expires {fmtDate(e.expires_at)}</p>}
                </div>
              )} />

            <DetailSection icon={CreditCard} title="Subscriptions" items={detail.subscriptions} empty="No subscriptions yet."
              render={(s) => (
                <div key={s.id} className="mb-2 rounded-xl border border-white/10 bg-elevated px-3 py-2 text-sm">
                  <div className="flex items-center justify-between">
                    <p className="text-white">{s.product_id || s.id}</p>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.status === 'active' || s.status === 'trialing' ? 'bg-success/15 text-success' : 'bg-white/10 text-white/50'}`}>{s.status}</span>
                  </div>
                  <p className="mt-1 text-xs text-white/40">{s.store} · started {fmtDate(s.starts_at)}{s.current_period_ends_at ? ` · renews ${fmtDate(s.current_period_ends_at)}` : ''}</p>
                </div>
              )} />

            <DetailSection icon={ShoppingBag} title="Purchases" items={detail.purchases} empty="No one-time purchases."
              render={(p) => (
                <div key={p.id} className="mb-2 rounded-xl border border-white/10 bg-elevated px-3 py-2 text-sm">
                  <p className="text-white">{p.product_id || p.id}</p>
                  <p className="text-xs text-white/40">{p.store} · {fmtDate(p.purchased_at)}</p>
                </div>
              )} />

            <div className="mt-8 border-t border-white/10 pt-5">
              <button onClick={remove} data-testid="admin-user-delete" className="flex w-full items-center justify-center gap-2 rounded-xl bg-destructive/15 px-4 py-2.5 text-sm font-semibold text-destructive hover:bg-destructive/25">
                <Trash2 className="h-4 w-4" /> Delete user from RevenueCat
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default function AdminUsers() {
  const [customers, setCustomers] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [connected, setConnected] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [metrics, setMetrics] = useState(null);

  const loadMore = useCallback((after) => {
    setLoadingMore(true);
    api.get('/admin/revenuecat/customers', { params: { limit: 50, ...(after ? { starting_after: after } : {}) } })
      .then((r) => {
        setConnected(r.data.connected);
        setCustomers((prev) => (after ? [...prev, ...r.data.customers] : r.data.customers));
        setCursor(r.data.next_starting_after);
      })
      .finally(() => { setLoadingMore(false); setLoading(false); });
  }, []);

  useEffect(() => {
    loadMore(null);
    api.get('/admin/revenuecat/metrics').then((r) => setMetrics(r.data));
  }, [loadMore]);

  const filtered = useMemo(() => {
    if (!search) return customers;
    const q = search.toLowerCase();
    return customers.filter((c) =>
      c.id.toLowerCase().includes(q) ||
      (c.last_seen_country || '').toLowerCase().includes(q) ||
      (c.last_seen_platform || '').toLowerCase().includes(q));
  }, [customers, search]);

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  if (!connected) {
    return (
      <div className="space-y-6">
        <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Users</h1>
        <EmptyState icon={UsersIcon} title="RevenueCat not connected" description="Add REVENUECAT_API_KEY to the backend — every mobile app user is registered in RevenueCat and will appear here." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Users</h1>
        <p className="mt-1 text-sm text-white/50">
          Every app user, live from RevenueCat.{metrics?.connected ? ` ${metrics.metrics.active_users ?? 0} active in the last 28 days · ${metrics.metrics.active_trials ?? 0} on trial.` : ''}
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter loaded users by ID, country or platform..."
          data-testid={ADMIN.usersSearch}
          className="w-full rounded-xl border border-white/10 bg-elevated py-2 pl-9 pr-3 text-sm text-white placeholder:text-white/40 focus:border-brand focus:outline-none"
        />
      </div>

      {customers.length === 0 ? (
        <EmptyState icon={UsersIcon} title="No users yet" description="Users appear here the moment they open your app — RevenueCat registers every install automatically." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-surface/70">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-white/40">
                <tr>
                  <th className="px-5 py-3 font-medium">User</th>
                  <th className="px-5 py-3 font-medium">Platform</th>
                  <th className="px-5 py-3 font-medium">Country</th>
                  <th className="px-5 py-3 font-medium">App Version</th>
                  <th className="px-5 py-3 font-medium">First Seen</th>
                  <th className="px-5 py-3 font-medium">Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} data-testid={ADMIN.userRow} onClick={() => setSelected(c.id)} className="cursor-pointer border-b border-white/5 transition-colors hover:bg-white/[0.03]">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand/15 text-brand"><Smartphone className="h-4 w-4" /></span>
                        <span className="font-mono text-xs text-white">{displayId(c.id)}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${PLATFORM_COLOR[c.last_seen_platform] || 'bg-white/10 text-white/70'}`}>{c.last_seen_platform || '—'}</span>
                    </td>
                    <td className="px-5 py-3 text-white/70">{c.last_seen_country || '—'}</td>
                    <td className="px-5 py-3 text-white/60">{c.last_seen_app_version || '—'}</td>
                    <td className="px-5 py-3 text-white/60">{c.first_seen_at ? new Date(c.first_seen_at).toLocaleDateString() : '—'}</td>
                    <td className="px-5 py-3 text-white/60">{c.last_seen_at ? new Date(c.last_seen_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-white/10 px-5 py-3">
            <span className="text-sm text-white/50">{filtered.length} of {customers.length} loaded{search ? ' (filtered)' : ''}</span>
            {cursor && (
              <button onClick={() => loadMore(cursor)} disabled={loadingMore} data-testid="admin-users-load-more" className="flex items-center gap-2 rounded-xl border border-white/10 bg-elevated px-4 py-2 text-sm text-white/80 hover:bg-white/5 disabled:opacity-40">
                {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />} Load more
              </button>
            )}
          </div>
        </div>
      )}

      {selected && (
        <UserDrawer
          userId={selected}
          onClose={() => setSelected(null)}
          onDeleted={(id) => { setSelected(null); setCustomers((prev) => prev.filter((x) => x.id !== id)); }}
        />
      )}
    </div>
  );
}
